import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import api from 'services/api';

import NoPedingData from 'assets/nopendingdata.svg';

import Card from 'components/Card';
import Button from 'components/Button';
import Widget from 'components/Widget';
import Tooltip from 'components/Tooltip';
import Pagination from 'components/Pagination';
import Avatar from 'components/Avatar';
import Loader from 'components/ContentLoader/GenericLoader';
import {
  FiBookOpen,
  FiCheckCircle,
  FiPlus,
  FiEdit,
  FiCoffee,
  FiAlertTriangle,
  FiEye,
  FiTrash2,
} from 'react-icons/fi';

import { Table, Td, Th } from 'styles/global';
import { Container, WidgetContent } from './styles';
import { AnuncioJornal } from 'pages/@types';
import AppError from 'utils/AppError';
import NotFound from 'components/NotFound';
import { Permissao, StatusAnuncioJornal } from 'utils/variables';
import { show } from 'store/modules/dialog/actions';
import { usePermit } from 'utils/hooks/usePermit';

interface Workflow {
  emprocesso: number;
  aprovacao: number;
  alteracao: number;
  enviado: number;
}
export default function Anuncios() {
  //  react-router-dom
  const navigate = useNavigate();

  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );
  const hasAvaliador = usePermit([], [Permissao.AVALIADOR]);
  /**
   * USE STATE
   */
  const [workflow, setWorkflow] = useState<Workflow>({
    emprocesso: 0,
    alteracao: 0,
    aprovacao: 0,
    enviado: 0,
  } as Workflow);
  const [anuncios, setAnuncios] = useState<AnuncioJornal[]>([]);
  const [selected, setSelected] = useState({} as AnuncioJornal);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(StatusAnuncioJornal.EMPROCESSO);

  /**
   * USE EFFECT
   */

  const handleClick = useCallback(a => {
    setStatus(a);
    setPage(1);
  }, []);

  const handleDelete = useCallback(
    anuncio => {
      setSelected(anuncio);
      const optionBase = {
        type: 'question',
        action: 'delete',
        contentHTML: `Remover <b>${anuncio.titulo}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/anunciojornal/${anuncio.id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Anúncio removido!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  useEffect(() => {
    const controller = new AbortController();
    async function loadAnuncio() {
      setLoading(true);
      try {
        const response = await api.get('/anunciojornal', {
          signal: controller.signal,
          params: {
            status,
            page,
          },
        });
        if (response && response.data) {
          setAnuncios(response.data.anuncios);
          setPages(response.data.pages);
          setWorkflow(response.data.workflow);
        } else toast.error('Falha de comunicação com o servidor');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadAnuncio();
    return () => {
      controller.abort();
    };
  }, [page, status]);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'success' && selected) {
        // Atualiza widgets removendo a solicitacao deletada
        setWorkflow(prevState => ({
          emprocesso:
            selected.status === StatusAnuncioJornal.EMPROCESSO
              ? prevState.emprocesso - 1
              : prevState.emprocesso,
          aprovacao:
            selected.status === StatusAnuncioJornal.APROVACAO
              ? prevState.aprovacao - 1
              : prevState.aprovacao,
          alteracao:
            selected.status === StatusAnuncioJornal.ALTERACAO
              ? prevState.alteracao - 1
              : prevState.alteracao,
          enviado:
            selected.status === StatusAnuncioJornal.ENVIADO
              ? prevState.enviado - 1
              : prevState.enviado,
        }));
        // Atualiza tabela removendo a solicitacao deletada
        setAnuncios(prevState =>
          prevState.filter(anuncio => anuncio.id !== selected.id),
        );
      }
    }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <WidgetContent>
        <Widget
          title="Em processo"
          type="condensed"
          icon={<FiBookOpen />}
          showLink={false}
          active={status === StatusAnuncioJornal.EMPROCESSO}
          color="info"
          loading={loading}
          handleClick={() => handleClick(StatusAnuncioJornal.EMPROCESSO)}
          data={workflow.emprocesso}
        />
        <Widget
          title="Aprovação"
          type="condensed"
          icon={<FiCoffee />}
          showLink={false}
          active={status === StatusAnuncioJornal.APROVACAO}
          color="warn"
          loading={loading}
          handleClick={() => handleClick(StatusAnuncioJornal.APROVACAO)}
          data={workflow.aprovacao}
        />
        <Widget
          title="Alteração"
          type="condensed"
          icon={<FiAlertTriangle />}
          showLink={false}
          active={status === StatusAnuncioJornal.ALTERACAO}
          color="danger"
          loading={loading}
          handleClick={() => handleClick(StatusAnuncioJornal.ALTERACAO)}
          data={workflow.alteracao}
        />
        <Widget
          title="Enviado"
          type="condensed"
          icon={<FiCheckCircle />}
          active={status === StatusAnuncioJornal.ENVIADO}
          color="success"
          loading={loading}
          handleClick={() => handleClick(StatusAnuncioJornal.ENVIADO)}
          data={workflow.enviado}
        />
      </WidgetContent>
      <Card>
        <header style={{ alignItems: 'baseline' }}>
          <h4>LISTA DE ANÚNCIOS PARA JORNAIS</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            href={`/anunciojornal/novo`}
            color="primary"
            tooltip="Criar novo Anúncio para Jornal"
          >
            NOVO ANÚNCIO
            <FiPlus />
          </Button>
        </header>
        {loading ? (
          <Loader type="chamada" />
        ) : anuncios && anuncios.length ? (
          <div style={{ padding: `0 15px` }}>
            <Table>
              <thead>
                <tr>
                  <th>TITULO</th>
                  <th style={{ width: 290 }}>JORNAIS</th>
                  <th style={{ width: 180 }}>FORMATO</th>
                  <Th style={{ width: 170 }}>VEICULAÇÃO</Th>
                  <th style={{ width: 120 }}>EDITOR</th>
                  <th style={{ width: 120 }}>AVALIADOR</th>
                  <th style={{ width: 60, textAlign: 'center' }}>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {anuncios.map(anuncio => (
                  <tr key={anuncio.id}>
                    <td>{anuncio.titulo}</td>
                    <td
                      style={{
                        whiteSpace: 'nowrap',
                        marginRight: '50rem',
                        textAlign: 'left',
                      }}
                    >
                      {anuncio.jornal.map(j => j.nome).join()}
                    </td>
                    <td>{anuncio.formato.toUpperCase()}</td>
                    <Td id={`id_datas_${anuncio.id}`}>
                      {anuncio.datainicio && anuncio.datafim
                        ? `de ${format(
                            parseISO(anuncio.datainicio),
                            'dd/MM',
                          )} a ${format(parseISO(anuncio.datafim), 'dd/MM')}`
                        : `${format(
                            parseISO(anuncio.datainicio),
                            'dd/MM/yyyy',
                          )}`}
                    </Td>
                    <td>
                      {anuncio.editor ? (
                        <Avatar
                          size="40"
                          name={anuncio.editor.nome}
                          src={anuncio.editor.avatar_url}
                          round
                        />
                      ) : (
                        <p>...</p>
                      )}
                    </td>
                    <td>
                      <Avatar
                        size="40"
                        name={anuncio.avaliador?.nome}
                        src={anuncio.avaliador?.avatar_url}
                        round
                      />
                    </td>
                    <td style={{ display: 'flex' }}>
                      {anuncio.download_url && (
                        <>
                          <Button
                            id={`id_anuncio_${anuncio.id}`}
                            fill
                            color="font"
                            round
                            onClick={e => {
                              e.preventDefault();
                              window.open(`${anuncio.download_url}`, '_blank');
                            }}
                          >
                            <FiEye />
                          </Button>
                          <Tooltip target={`id_anuncio_${anuncio.id}`}>
                            VISUALIZAR
                          </Tooltip>
                        </>
                      )}
                      <Button
                        id={`id_editar_${anuncio.id}`}
                        href={`/anunciojornal/${anuncio.id}`}
                        fill
                        color="font"
                        round
                        disabled={status === StatusAnuncioJornal.ENVIADO}
                      >
                        <FiEdit />
                      </Button>
                      {hasAvaliador &&
                      status === StatusAnuncioJornal.APROVACAO ? (
                        <Tooltip target={`id_editar_${anuncio.id}`}>
                          AVALIAR
                        </Tooltip>
                      ) : (
                        <Tooltip target={`id_editar_${anuncio.id}`}>
                          EDITAR
                        </Tooltip>
                      )}
                      <Button
                        id={`id_deletar_${anuncio.id}`}
                        fill
                        color="font"
                        round
                        disabled={status === StatusAnuncioJornal.ENVIADO}
                        onClick={() => {
                          handleDelete(anuncio);
                        }}
                      >
                        <FiTrash2 />
                      </Button>
                      <Tooltip target={`id_deletar_${anuncio.id}`}>
                        DELETAR
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <NotFound img={NoPedingData} title="Anúncios">
            Nenhum ANÚNCIO encontrado
          </NotFound>
        )}
      </Card>
    </Container>
  );
}
